import {
  MASCOT_AUTO,
  MASCOT_NONE,
  type ArtStyleMascot,
  type ArtStyleSummary,
  type MascotPick,
} from '../../../art-style/art-style-api';
import type { MascotChoice } from '../../../art-style/useStyleChoice';

/** A translate function (`t` from react-i18next, or a stand-in in tests). */
export type Translate = (key: string) => string;

/** The cast an ad can draw: the style's mascots that have at least one image (the same filter as MascotPicker, so a
 *  Character section never shows around a picker that hides itself). Pure. */
export const adCast = (mascots: readonly ArtStyleMascot[] | undefined): ArtStyleMascot[] =>
  (mascots ?? []).filter((mascot) => mascot.images.length > 0);

/** A closed Character section's summary: "No character", the picked mascot's name, else "Auto" (a pick the cast no
 *  longer has reads as Auto, as the picker and the service treat it). Pure. */
export const adCharacterSummary = (cast: readonly ArtStyleMascot[], pick: MascotPick, t: Translate): string => {
  if (pick === MASCOT_NONE) return t('No character');
  const picked = cast.find((mascot) => mascot.id === pick);
  if (!picked) return t('Auto');
  return picked.name.trim() || t('Unnamed character');
};

/** A closed Art style section's summary: nothing while the brand's style is still being read (the header then shows
 *  none), the style's name once there is one, else "None active". Pure. */
export const adStyleSummary = (loaded: boolean, styleName: string | null, t: Translate, failed = false): string => {
  if (!loaded) return '';
  if (failed) return t("Couldn't be read. Reload the page to try again.");
  return styleName ?? t('None active');
};

/** An ad run's art style choice as a composer holds it (a run option, never part of the document). */
export interface AdStyleChoice {
  /** The owner's style pick: undefined while none is made (the brand's primary style draws), '' for "No style"
   *  (offered only while the brand has no primary), else a style's id. */
  style: string | undefined;
  /** The character pick, with the style it was made for: it counts only while the run draws that style
   *  (pickedMascotOf), so a style switch (or a pick that falls back) never sends another cast's character. */
  mascot: MascotChoice;
}

/** What a version records it was drawn with (`art_style`), as far as the choice needs it. */
export type AdDrawnStyle = { style_id?: string; mascot_pick?: string };

/** The choice a version was drawn with: its style (when it had one) and its character pick, made for that style. A
 *  version drawn with no style (or never drawn) leaves the style to the brand's primary. Pure. */
export const adStyleChoiceOf = (drawn: AdDrawnStyle | undefined): AdStyleChoice => ({
  style: drawn?.style_id || undefined,
  mascot: { style: drawn?.style_id ?? '', pick: drawn?.mascot_pick ?? MASCOT_AUTO },
});

/** "No style" is offered while the brand has styles but no primary one (it then draws with none unless one is
 *  picked). Pure. */
export const adStyleAllowsNone = (styles: readonly ArtStyleSummary[], defaultId: string | undefined): boolean =>
  styles.length > 0 && defaultId === undefined;

/** The picker shows when it offers more than one choice ("No style" counts). Pure. */
export const adStylePickerShown = (styles: readonly ArtStyleSummary[], defaultId: string | undefined): boolean =>
  styles.length + (adStyleAllowsNone(styles, defaultId) ? 1 : 0) > 1;

/**
 * The style an ad run draws in ('' for none): the pick while the latest list still has it ('' while "No style" is
 * offered), else the brand's primary, else `fallback` (the active style read on its own, used when the list could not
 * be read). Pure.
 */
export const resolveAdStyle = (
  pick: string | undefined,
  styles: readonly ArtStyleSummary[],
  defaultId: string | undefined,
  fallback: string,
): string => {
  if (pick === '' && adStyleAllowsNone(styles, defaultId)) return '';
  if (pick && styles.some((style) => style.id === pick)) return pick;
  return defaultId ?? fallback;
};
